import type { IteroAssetType } from '../../../../../services/iteroBrowse';
import type { AssetDownloadState, AssetOption } from '../assetDownload.ts';
import { tallyCount, type ImageTally } from './scanTally.ts';

export type FileRowTone = 'muted' | 'success' | 'warning' | 'error';

export interface FileRowNote {
    tone: FileRowTone;
    text: string;
}

export interface FileRowView {
    title: string;
    subline: string;
    note: FileRowNote | null;
    isBusy: boolean;
    canDownload: boolean;
}

const NONE_FOR_ORDER = 'None for this order';

const plural = (count: number, singular: string, many: string) =>
    `${count.toLocaleString('en-US')} ${count === 1 ? singular : many}`;

function imagesSubline(
    tally: ImageTally | null,
    fallback: string,
    describe: (n: number) => string
) {
    if (tally?.kind === 'none') return NONE_FOR_ORDER;
    const count = tally ? tallyCount(tally) : null;
    return count === null ? fallback : describe(count);
}

const SUBLINES: Readonly<Record<IteroAssetType, (tally: ImageTally | null) => string>> = {
    IDE: () => 'iDE zip · STL, Rx and scan data',
    IMAGE_GALLERY: (tally) =>
        imagesSubline(tally, 'JPG views', (count) => plural(count, 'JPG view', 'JPG views')),
    SCREEN_CAPTURE: (tally) =>
        imagesSubline(tally, 'From the scanner', (count) =>
            plural(count, 'image from the scanner', 'images from the scanner')
        )
};

export function fileRowNote(state: AssetDownloadState): FileRowNote | null {
    switch (state.status) {
        case 'idle':
            return null;
        case 'downloading':
            return { tone: 'muted', text: 'Downloading. Large scans can take several minutes.' };
        case 'saved':
            return { tone: 'success', text: 'Downloaded' };
        case 'generating':
            return { tone: 'warning', text: 'Preparing. Try again in about a minute.' };
        case 'empty':
            return { tone: 'muted', text: NONE_FOR_ORDER };
        case 'error':
            return { tone: 'error', text: state.message };
    }
}

// The tally comes from the gallery's own load: a set known to be empty has nothing to download.
export function fileRowView(
    option: AssetOption,
    download: AssetDownloadState,
    tally: ImageTally | null
): FileRowView {
    const isBusy = download.status === 'downloading';
    return {
        title: option.label,
        subline: SUBLINES[option.type](tally),
        note: fileRowNote(download),
        isBusy,
        canDownload: !isBusy && tally?.kind !== 'none'
    };
}
